<script lang="ts" setup>
import { computed } from "vue";
import type { PartnerStats } from "@/types/project-types";

const props = defineProps<{
  stats: PartnerStats;
  loading?: boolean;
}>();

const successRateColor = computed(() => {
  if (props.stats.avgSuccessRate >= 95) return "text-success";
  if (props.stats.avgSuccessRate >= 80) return "text-warning";
  return "text-danger";
});

const criticalIssuesColor = computed(() => {
  if (props.stats.criticalIssues === 0) return "text-success";
  if (props.stats.criticalIssues <= 2) return "text-warning";
  return "text-danger";
});
</script>

<template>
  <div class="partner-stats p-0 p-md-2">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else class="row g-2 g-md-4">
      <!-- Total Clients -->
      <div class="col-4 col-md-2">
        <div class="card text-center h-100">
          <div class="card-body">
            <i class="bi bi-people fs-1 text-primary mb-2"></i>
            <h3 class="mb-2">{{ stats.totalClients }}</h3>
            <p class="card-subtitle mb-2 text-muted">Total Clients</p>
            <small class="text-success">
              {{ stats.activeClients }} active
            </small>
          </div>
        </div>
      </div>

      <!-- Total Projects -->
      <div class="col-4 col-md-2">
        <div class="card text-center h-100">
          <div class="card-body">
            <i class="bi bi-folder fs-1 mb-2 text-secondary"></i>
            <h3 class="mb-2">{{ stats.totalProjects }}</h3>
            <p class="card-subtitle mb-2 text-muted">Total Projects</p>
          </div>
        </div>
      </div>

      <!-- Total Runs -->
      <div class="col-4 col-md-2">
        <div class="card text-center h-100">
          <div class="card-body">
            <i class="bi bi-play-circle fs-1 text-info mb-2"></i>
            <h3 class="mb-2">{{ stats.totalRuns.toLocaleString() }}</h3>
            <p class="card-subtitle mb-2 text-muted">Total Runs</p>
            <small class="text-success">
              {{ stats.successfulRuns.toLocaleString() }} success
            </small>
          </div>
        </div>
      </div>

      <!-- Success Rate -->
      <div class="col-4 col-md-2">
        <div class="card text-center h-100">
          <div class="card-body">
            <i
              class="bi bi-check-circle fs-1 mb-2"
              :class="successRateColor"
            ></i>
            <h3 class="mb-2" :class="successRateColor">
              {{ stats.avgSuccessRate.toFixed(1) }}%
            </h3>
            <p class="card-subtitle mb-2 text-muted">Avg Success Rate</p>
          </div>
        </div>
      </div>

      <!-- Critical Issues -->
      <div class="col-4 col-md-2">
        <div class="card text-center h-100">
          <div class="card-body">
            <i
              class="bi bi-exclamation-triangle fs-1 mb-2"
              :class="criticalIssuesColor"
            ></i>
            <h3 class="mb-2" :class="criticalIssuesColor">
              {{ stats.criticalIssues }}
            </h3>
            <p class="card-subtitle mb-2 text-muted">Critical Issues</p>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="col-4 col-md-2">
        <div class="card text-center h-100">
          <div class="card-body">
            <i class="bi bi-activity fs-1 text-secondary mb-2"></i>
            <h3 class="mb-2">{{ stats.recentActivityCount }}</h3>
            <p class="card-subtitle mb-2 text-muted">Recent Activities</p>
            <small class="text-muted">Last 24 hours</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';
@import 'bootstrap/scss/mixins';

// Three stat cards per row on mobile (col-4) leaves each card narrow, so scale the contents down
// below md: smaller icon/number/label and tighter padding keep the icon, value and label on their
// own lines without wrapping. From md up the cards are wide (6-up) and use the full-size styling.
@include media-breakpoint-down(md) {
  .partner-stats .card-body {
    padding: 0.5rem 0.25rem;
  }

  // Icon (.fs-1 in markup)
  .partner-stats .card-body > i {
    font-size: 1.5rem !important;
    margin-bottom: 0.25rem !important;
  }

  // Stat value (<h3>)
  .partner-stats .card-body h3 {
    font-size: 1.25rem;
    margin-bottom: 0.25rem !important;
  }

  // Label + sub-label
  .partner-stats .card-subtitle {
    font-size: 0.7rem;
    line-height: 1.2;
    margin-bottom: 0.25rem !important;
  }

  .partner-stats .card-body small {
    font-size: 0.65rem;
  }
}
</style>
